import { sql } from 'drizzle-orm';
import {
  pgTable,
  index,
  timestamp,
  varchar,
  text,
  integer,
  boolean,
  uuid,
  jsonb,
  uniqueIndex,
} from 'drizzle-orm/pg-core';
import { organizations } from './organizations';
import { users } from './users';

/**
 * Invites (beta invites, beta codes, and firm/team invites) share one MANDATORY,
 * fixed 7-day expiry window. It is always computed on the server as `now + 7 days` at
 * create / resend / re-activate time; any client-supplied `expiresAt` is ignored. This
 * is the single source of truth for the duration. For beta invites and codes a `null`
 * expiresAt only ever occurs on legacy rows written before this policy and is treated
 * as already expired everywhere it is validated (fail closed); firm invites have always
 * been created with a server-set expiry, so they have no null-expiry legacy population.
 */
export const INVITE_EXPIRY_DAYS = 7;
export const INVITE_EXPIRY_MS = INVITE_EXPIRY_DAYS * 24 * 60 * 60 * 1000;

/** Server-authoritative expiry for a new / resent / re-activated invite or code. */
export function inviteExpiryFrom(now: Date = new Date()): Date {
  return new Date(now.getTime() + INVITE_EXPIRY_MS);
}

/** Beta-named aliases of the generic invite-expiry symbols above (same 7-day window). */
export const BETA_EXPIRY_DAYS = INVITE_EXPIRY_DAYS;
export const BETA_EXPIRY_MS = INVITE_EXPIRY_MS;
export const betaExpiryFrom = inviteExpiryFrom;

export const betaInvites = pgTable('beta_invites', {
  id: uuid('id').primaryKey().default(sql`gen_random_uuid()`),
  email: varchar('email', { length: 320 }).notNull(),
  invitedBy: varchar('invited_by').references(() => users.id, { onDelete: 'set null' }),
  note: text('note'),
  usedAt: timestamp('used_at'),
  // Superadmin beta-invite management: revoke, expiry, and resend tracking.
  revokedAt: timestamp('revoked_at'),
  expiresAt: timestamp('expires_at'),
  lastSentAt: timestamp('last_sent_at'),
  sendCount: integer('send_count').notNull().default(0),
  createdAt: timestamp('created_at').defaultNow().notNull(),
}, (table) => [
  uniqueIndex('beta_invites_email_unique').on(table.email),
]);

export const betaCodes = pgTable('beta_codes', {
  id: uuid('id').primaryKey().default(sql`gen_random_uuid()`),
  code: varchar('code', { length: 64 }).notNull(),
  createdBy: varchar('created_by').references(() => users.id, { onDelete: 'set null' }),
  maxUses: integer('max_uses').notNull().default(1),
  useCount: integer('use_count').notNull().default(0),
  isActive: boolean('is_active').notNull().default(true),
  expiresAt: timestamp('expires_at'),
  createdAt: timestamp('created_at').defaultNow().notNull(),
}, (table) => [
  uniqueIndex('beta_codes_code_unique').on(table.code),
]);

export const firmInvites = pgTable('firm_invites', {
  id: uuid('id').primaryKey().default(sql`gen_random_uuid()`),
  orgId: uuid('org_id').notNull().references(() => organizations.id, { onDelete: 'cascade' }),
  email: varchar('email', { length: 320 }).notNull(),
  token: varchar('token', { length: 128 }).notNull(),
  hasClaimsAccess: boolean('has_claims_access').notNull().default(true),
  canManageBilling: boolean('can_manage_billing').notNull().default(false),
  canAccessAccounting: boolean('can_access_accounting').notNull().default(false),
  invitedBy: varchar('invited_by').references(() => users.id, { onDelete: 'set null' }),
  inviteLink: text('invite_link'),
  acceptedAt: timestamp('accepted_at'),
  expiresAt: timestamp('expires_at'),
  createdAt: timestamp('created_at').defaultNow().notNull(),
}, (table) => [
  uniqueIndex('firm_invites_token_unique').on(table.token),
  index('firm_invites_org_id_idx').on(table.orgId),
  index('firm_invites_email_idx').on(table.email),
]);

export const stripeWebhookEvents = pgTable('stripe_webhook_events', {
  id: uuid('id').primaryKey().default(sql`gen_random_uuid()`),
  stripeEventId: varchar('stripe_event_id', { length: 255 }).notNull(),
  orgId: uuid('org_id').references(() => organizations.id, { onDelete: 'cascade' }),
  type: varchar('type', { length: 128 }).notNull(),
  processedAt: timestamp('processed_at').defaultNow().notNull(),
  payload: jsonb('payload'),
}, (table) => [
  uniqueIndex('stripe_webhook_events_event_id_unique').on(table.stripeEventId),
  index('stripe_webhook_events_org_id_idx').on(table.orgId),
]);

export type BetaInvite = typeof betaInvites.$inferSelect;
export type InsertBetaInvite = typeof betaInvites.$inferInsert;
export type BetaCode = typeof betaCodes.$inferSelect;
export type InsertBetaCode = typeof betaCodes.$inferInsert;
export type FirmInvite = typeof firmInvites.$inferSelect;
export type InsertFirmInvite = typeof firmInvites.$inferInsert;
export type StripeWebhookEvent = typeof stripeWebhookEvents.$inferSelect;
export type InsertStripeWebhookEvent = typeof stripeWebhookEvents.$inferInsert;
